import { isToday } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { InsightDetails } from '~/components/insights-group/insight-card/insights-details/InsightDetails';
import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { useMarkEventRead } from '~/react-query-hooks/events/use-event-feed/useEventFeed';
import { appPaths } from '~/routes/app-paths';
import { isOnPage } from '~/services/tracking/posthog/utils';
import { getExploreDirection } from '~/views/dashboard/whats-new-widget/components/insight-card/utils';
import { HORIZONTAL_TABLE_COMBINE_SIZE } from '~/views/explore/components/truncate-combined-icons/TruncateCombinedIcons';
import {
  TABLE_SINGLE_HEIGHT,
  TABLE_SINGLE_WIDTH,
} from '~/views/explore/components/truncate-single-icons/TruncateSingleIcons';
import { InsightIconsRoot } from '~/views/insights/components/insight-icons-root/InsightIconsRoot';

import * as S from './ClickableScenarioInsightTooltip.styles';
import { ScenarioInsightWithEvent } from '~/react-query-hooks/events/use-event-feed/scenario-from-events';

interface ClickableScenarioInsightTooltipProps {
  insights: ScenarioInsightWithEvent[];
  entityTag: string;
}

export const ClickableScenarioInsightTooltip = ({
  insights,
  entityTag,
}: ClickableScenarioInsightTooltipProps) => {
  const { t } = useTranslation(['insights']);
  const { openDrawer } = useInsightDrawer();
  const { mutate: markEventRead } = useMarkEventRead();
  const isOnWatchlist = isOnPage(appPaths.watchlist);

  return (
    <>
      {insights.map(insight => {
        const {
          median_return: median,
          horizon,
          stars,
          conditions: insightConditions,
        } = insight.metadata;
        const episodeLabel = t('insights:insightCard.episode', {
          count: insight.metadata.episode_count,
        });
        const isCombined = insight.metadata.kind === 'combination';
        const isStarred = isToday(new Date(insight.created_at));
        const direction = getExploreDirection(insight.metadata.direction);
        const customSize = isCombined
          ? HORIZONTAL_TABLE_COMBINE_SIZE
          : {
              width: `${TABLE_SINGLE_WIDTH}px`,
              height: `${TABLE_SINGLE_HEIGHT}px`,
            };
        const conditions = isCombined
          ? insightConditions
          : [insightConditions[0]];

        const handleClick = () => {
          if (insight.eventId) {
            markEventRead(insight.eventId);
          }
          openDrawer(insight.id, {
            source: isOnWatchlist ? 'watchlist' : 'dashboard_watchlist',
          });
        };

        return (
          <div key={insight.id} onClick={handleClick}>
            <S.TooltipWrapper $isRead={insight.read}>
              <InsightIconsRoot
                conditions={conditions}
                direction={direction}
                isStarred={isStarred}
                customSize={customSize}
                isCombined={isCombined}
                layout={isCombined ? 'horizontal' : 'vertical'}
              />
              <InsightDetails
                isTooltip
                median={median}
                horizon={horizon}
                name={insight.card.name}
                stars={stars}
                direction={direction}
                isNewInsight={isStarred}
                entityTag={entityTag}
                episodeLabel={episodeLabel}
              />
            </S.TooltipWrapper>
          </div>
        );
      })}
    </>
  );
};
